import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { subDays } from "date-fns";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import { DonutChart, HorizontalBarChart, InlineAlert, StackedBarChart, WordCloud, Metric } from "@xapp/components";
import { AppLeadBucket, BusinessHoursSource, CategoryBucket, DayOfWeekBucket, TermBucket, ZipEngagementBucket } from "generated/graphql";
import { DashboardDateFilter } from "components/DashboardDateFilter";
import { PageHeader } from "components/PageHeader";
import { DashboardFilterBar } from "components/DashboardFilterBar";
import type { DashboardFilterType, DashboardFilterValue, DashboardActiveFilters, AvailableFilterOptions } from "components/DashboardFilterBar";
import { formatLabel } from "utils/formatLabel";
import styles from "./styles/LeadsDashboard.module.scss";

// ============================================
// Constants
// ============================================

/** Maximum number of job types to display in charts */
const MAX_JOB_TYPES_DISPLAYED = 10;

/** Named slices a legend pie draws before rolling the long tail into an expandable "Other" */
const MAX_PIE_SLICES = 8;

/** Maximum number of terms to display in word cloud */
const MAX_WORD_CLOUD_TERMS = 20;

/** Number of zip rows shown by default before expanding */
const COLLAPSED_ZIP_ROW_COUNT = 3;

/** Standard chart dimensions */
const CHART_DIMENSIONS = {
    WIDTH: 320,
    HEIGHT: 320,
    BAR_CHART_HEIGHT: 400
} as const;

/** Date range configuration */
const DATE_RANGE_CONFIG = {
    /** Default date range in days */
    DEFAULT_DAYS_BACK: 30
} as const;

/** Word cloud height */
const WORD_CLOUD_HEIGHT = 400;

// Chart color palette - diverse colors for better distinction
const CHART_COLORS = [
    '#00c04b', // Green
    '#8884d8', // Purple
    '#ffc658', // Yellow
    '#ff8042', // Orange
    '#00bcd4', // Cyan
    '#e91e63', // Pink
    '#9c27b0', // Deep Purple
    '#3f51b5', // Indigo
    '#009688', // Teal
    '#ff5722'  // Deep Orange
];

// Helper to add colors to data
const addColorsToData = <T extends { name: string; value: number }>(data: T[]): (T & { color: string })[] => {
    return data.map((item, index) => ({
        ...item,
        color: CHART_COLORS[index % CHART_COLORS.length]
    }));
};

// Re-export filter types from DashboardFilterBar
export type { DashboardFilterType, DashboardFilterValue, DashboardActiveFilters, AvailableFilterOptions } from "components/DashboardFilterBar";

/**
 * Granular status of the app's Google Business Profile place configuration.
 * Used to show contextual guidance under the day-of-week chart.
 */
export type PlaceConfigStatus = "loading" | "no-place" | "missing-address" | "missing-hours" | "configured";

export interface LeadsDashboardViewProps {
    totalLeads?: number;
    highValueLeadsPercentage?: number;
    leadsBySource?: CategoryBucket[];
    leadsByReferringSource?: CategoryBucket[];
    leadsByContactType?: CategoryBucket[];
    leadsByServiceType?: CategoryBucket[];
    leadsByJobType?: CategoryBucket[];
    leadsByUrgency?: CategoryBucket[];
    /** How the customer reached the business - phone / form / chat / email */
    leadsByContactMethod?: CategoryBucket[];
    /** Who/what handled the engagement - ai / human / self_serve / unknown */
    leadsByAgentType?: CategoryBucket[];
    /** Vendor/tech platform that captured the engagement - xapp-ai / avoca / servicetitan / jobber */
    leadsByCapturePlatform?: CategoryBucket[];
    /** System of record that delivered the engagement */
    leadsBySourceSystem?: CategoryBucket[];
    /** The specific surface that captured the engagement - form-widget / chat-widget / servicetitan-phone / ... */
    leadsByCaptureSurface?: CategoryBucket[];
    /** Marketing UTM source. Sparse - most apps will have little to no data here */
    leadsByUtmSource?: CategoryBucket[];
    /** Marketing UTM medium. Sparse - most apps will have little to no data here */
    leadsByUtmMedium?: CategoryBucket[];
    /** Marketing UTM campaign. Sparse - most apps will have little to no data here */
    leadsByUtmCampaign?: CategoryBucket[];
    /** Leads aggregated by day of week with business hours breakdown */
    leadsByDayOfWeek?: DayOfWeekBucket[];
    /** Percentage of leads occurring outside business hours */
    outsideBusinessHoursPercentage?: number;
    /** Source used for business hours calculation (PLACE, APP, or DEFAULT) */
    businessHoursSource?: BusinessHoursSource;
    /** Human-readable business hours summary for each day of the week */
    businessHoursSummary?: string[];
    topTerms?: TermBucket[];
    /** Leads aggregated by app - only available at org level */
    leadsByApp?: AppLeadBucket[];
    /** Top engagements by zip code */
    topEngagementsByZip?: ZipEngagementBucket[];
    /** Leads aggregated by organization - only available at global level */
    leadsByOrganization?: Array<{
        organizationId: string;
        name: string;
        leadCount: number;
        highValueLeadCount: number;
    }>;
    /** Organization ID - needed for leadsByApp links */
    organizationId?: string;
    loading?: boolean;
    startDate?: string;
    endDate?: string;
    onDateRangeChange?: (startDate: string | undefined, endDate: string | undefined) => void;
    /**
     * When true, only renders the charts without the header/date controls
     */
    embedded?: boolean;
    /**
     * Callback when user clicks a chart segment to apply a filter to the dashboard
     */
    onFilterApply?: (filter: DashboardFilterValue) => void;
    /**
     * Callback when user clicks to view leads with all active filters
     */
    onViewLeads?: () => void;
    /**
     * Currently active filters to display in the filter bar
     */
    activeFilters?: DashboardActiveFilters;
    /**
     * Callback to clear a specific filter
     */
    onFilterClear?: (filterType: DashboardFilterType) => void;
    /**
     * Callback to clear all filters
     */
    onClearAllFilters?: () => void;
    /**
     * Available filter options for the Add Filter menu (derived from chart data if not provided)
     */
    availableFilterOptions?: AvailableFilterOptions;
    /**
     * URL to app settings page for configuring places/business hours.
     * When provided, banners will include a link to this URL.
     */
    businessHoursSettingsUrl?: string;
    /**
     * Granular status of the app's place configuration.
     * Determines what guidance to show under the day-of-week chart.
     */
    placeConfigStatus?: PlaceConfigStatus;
    /**
     * Callback to refresh a place's data from Google Business Profile.
     * Shown when placeConfigStatus is "missing-hours".
     */
    onRefreshPlace?: () => void;
    /**
     * Whether a place refresh is currently in progress.
     */
    refreshingPlace?: boolean;
    /**
     * Whether to show the organization filter in the Add Filter menu.
     * Only shown on global dashboard view.
     */
    showOrganizationFilter?: boolean;
    /**
     * Available organizations for the organization filter dropdown.
     */
    availableOrganizations?: Array<{ organizationId: string; name: string }>;
    /**
     * Currently selected organization IDs for filtering.
     */
    selectedOrganizationIds?: string[];
    /**
     * Callback when organization filter selection changes.
     */
    onOrganizationFilterChange?: (organizationIds: string[]) => void;
}

export const LeadsDashboardView: FC<LeadsDashboardViewProps> = ({
    totalLeads,
    highValueLeadsPercentage,
    leadsBySource = [],
    leadsByReferringSource = [],
    leadsByContactType = [],
    leadsByServiceType = [],
    leadsByJobType = [],
    leadsByUrgency = [],
    leadsByContactMethod = [],
    leadsByAgentType = [],
    leadsByCapturePlatform = [],
    leadsBySourceSystem = [],
    leadsByCaptureSurface = [],
    leadsByUtmSource = [],
    leadsByUtmMedium = [],
    leadsByUtmCampaign = [],
    leadsByDayOfWeek = [],
    outsideBusinessHoursPercentage,
    businessHoursSource,
    businessHoursSummary,
    topTerms = [],
    leadsByApp,
    topEngagementsByZip,
    leadsByOrganization,
    organizationId,
    loading = false,
    startDate,
    endDate,
    onDateRangeChange,
    embedded = false,
    onFilterApply,
    onViewLeads,
    activeFilters = {},
    onFilterClear,
    onClearAllFilters,
    availableFilterOptions,
    businessHoursSettingsUrl,
    placeConfigStatus,
    onRefreshPlace,
    refreshingPlace = false,
    showOrganizationFilter = false,
    availableOrganizations = [],
    selectedOrganizationIds = [],
    onOrganizationFilterChange
}) => {
    const now = useMemo(() => new Date(), []);

    // Parse startDate/endDate strings or use defaults
    const [start, setStart] = useState<Date>(() => {
        if (startDate) return new Date(startDate);
        return subDays(now, DATE_RANGE_CONFIG.DEFAULT_DAYS_BACK);
    });
    const [end, setEnd] = useState<Date>(() => {
        if (endDate) return new Date(endDate);
        return now;
    });

    // Sync local state when props change
    useEffect(() => {
        if (startDate) {
            setStart(new Date(startDate));
        }
    }, [startDate]);

    useEffect(() => {
        if (endDate) {
            setEnd(new Date(endDate));
        }
    }, [endDate]);

    const handleDateRangeChange = useCallback((newStart: Date, newEnd: Date) => {
        // Validate that start date is before end date
        if (newStart > newEnd) {
            return;
        }
        setStart(newStart);
        setEnd(newEnd);
        onDateRangeChange?.(newStart.toISOString(), newEnd.toISOString());
    }, [onDateRangeChange]);

    // Convert data for charts with colors applied, formatted labels, and original keys
    const sourceData = useMemo(() =>
        addColorsToData(leadsBySource.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            percentage: item.percentage,
            originalKey: item.key
        }))),
        [leadsBySource]
    );

    const referringSourceData = useMemo(() =>
        addColorsToData(leadsByReferringSource.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            percentage: item.percentage,
            originalKey: item.key
        }))),
        [leadsByReferringSource]
    );

    const contactTypeData = useMemo(() =>
        addColorsToData(leadsByContactType.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByContactType]
    );

    const serviceTypeData = useMemo(() =>
        addColorsToData(leadsByServiceType.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByServiceType]
    );

    const jobTypeData = useMemo(() =>
        addColorsToData(leadsByJobType.slice(0, MAX_JOB_TYPES_DISPLAYED).map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByJobType]
    );

    const urgencyData = useMemo(() =>
        addColorsToData(leadsByUrgency.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByUrgency]
    );

    const contactMethodData = useMemo(() =>
        addColorsToData(leadsByContactMethod.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByContactMethod]
    );

    const agentTypeData = useMemo(() =>
        addColorsToData(leadsByAgentType.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByAgentType]
    );

    const capturePlatformData = useMemo(() =>
        addColorsToData(leadsByCapturePlatform.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByCapturePlatform]
    );

    const sourceSystemData = useMemo(() =>
        addColorsToData(leadsBySourceSystem.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsBySourceSystem]
    );

    const captureSurfaceData = useMemo(() =>
        addColorsToData(leadsByCaptureSurface.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByCaptureSurface]
    );

    const utmSourceData = useMemo(() =>
        addColorsToData(leadsByUtmSource.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByUtmSource]
    );

    const utmMediumData = useMemo(() =>
        addColorsToData(leadsByUtmMedium.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByUtmMedium]
    );

    const utmCampaignData = useMemo(() =>
        addColorsToData(leadsByUtmCampaign.map(item => ({
            name: formatLabel(item.key),
            value: item.count,
            originalKey: item.key
        }))),
        [leadsByUtmCampaign]
    );

    // Marketing attribution panels are sparse on most apps (utm ~3% of leads for many).
    // Hide the whole cluster rather than show three permanently-blank cards.
    const hasMarketingAttributionData = loading ||
        utmSourceData.length > 0 ||
        utmMediumData.length > 0 ||
        utmCampaignData.length > 0;

    // Transform day of week data for stacked bar chart
    const dayOfWeekChartData = useMemo(() =>
        leadsByDayOfWeek.map(item => ({
            name: item.dayName,
            primaryValue: item.businessHoursCount,
            secondaryValue: item.nonBusinessHoursCount,
            total: item.totalCount
        })),
        [leadsByDayOfWeek]
    );

    // Calculate outside business hours percentage summary
    const outsideBusinessHoursSummary = useMemo(() => {
        if (outsideBusinessHoursPercentage !== undefined) {
            return `${outsideBusinessHoursPercentage.toFixed(1)}% of contacts occur outside business hours`;
        }
        // Calculate from data if percentage not provided
        if (leadsByDayOfWeek.length > 0) {
            const totalContacts = leadsByDayOfWeek.reduce((sum, d) => sum + d.totalCount, 0);
            const totalOutside = leadsByDayOfWeek.reduce((sum, d) => sum + d.nonBusinessHoursCount, 0);
            if (totalContacts > 0) {
                const percentage = (totalOutside / totalContacts) * 100;
                return `${percentage.toFixed(1)}% of contacts occur outside business hours`;
            }
        }
        return undefined;
    }, [leadsByDayOfWeek, outsideBusinessHoursPercentage]);

    // Calculate counts for specific contact types
    const totalServiceRequests = useMemo(() => {
        const serviceRequest = leadsByContactType.find(item => item.key === 'SERVICE_REQUEST');
        return serviceRequest?.count ?? 0;
    }, [leadsByContactType]);

    const totalQuoteRequests = useMemo(() => {
        const quoteRequest = leadsByContactType.find(item => item.key === 'QUOTE_REQUEST');
        return quoteRequest?.count ?? 0;
    }, [leadsByContactType]);

    const totalOtherRequests = useMemo(() => {
        const total = totalLeads ?? 0;
        return total - totalServiceRequests - totalQuoteRequests;
    }, [totalLeads, totalServiceRequests, totalQuoteRequests]);

    // High Value Leads data for donut chart
    const highValueLeadsData = useMemo(() => {
        if (totalLeads === undefined || highValueLeadsPercentage === undefined) return [];
        const highValueCount = Math.round((totalLeads * highValueLeadsPercentage) / 100);
        const regularCount = totalLeads - highValueCount;
        return addColorsToData([
            { name: 'High Value', value: highValueCount, originalKey: 'true' },
            { name: 'Regular', value: regularCount, originalKey: 'false' }
        ]);
    }, [totalLeads, highValueLeadsPercentage]);

    // Create click handler factory for charts
    const createChartClickHandler = useCallback((filterType: DashboardFilterType) => {
        if (!onFilterApply) return undefined;
        return (dataPoint: { name: string; originalKey?: string }) => {
            const filter: DashboardFilterValue = {
                filterType,
                value: dataPoint.originalKey || dataPoint.name,
                displayName: dataPoint.name
            };
            onFilterApply(filter);
        };
    }, [onFilterApply]);

    // Click handler for zip code filter
    const handleZipFilterClick = useCallback((zipCode: string) => {
        if (!onFilterApply) return;
        onFilterApply({
            filterType: 'zipCode',
            value: zipCode,
            displayName: zipCode
        });
    }, [onFilterApply]);

    // Top Engagements by Zip Code - collapsed/expanded state
    const [zipRowsExpanded, setZipRowsExpanded] = useState(false);

    const visibleZipEngagements = useMemo(() => {
        if (!topEngagementsByZip) return topEngagementsByZip;
        if (zipRowsExpanded) return topEngagementsByZip;
        return topEngagementsByZip.slice(0, COLLAPSED_ZIP_ROW_COUNT);
    }, [topEngagementsByZip, zipRowsExpanded]);

    const handleToggleZipRowsExpanded = useCallback(() => {
        setZipRowsExpanded(prev => !prev);
    }, []);

    // Derive available filter options from chart data if not explicitly provided
    const derivedFilterOptions = useMemo(() => {
        const options = availableFilterOptions || {};
        return {
            sources: options.sources || leadsBySource.map(item => item.key),
            referringSources: options.referringSources || leadsByReferringSource.map(item => item.key),
            contactTypes: options.contactTypes || leadsByContactType.map(item => item.key),
            serviceTypes: options.serviceTypes || leadsByServiceType.map(item => item.key),
            jobTypes: options.jobTypes || leadsByJobType.map(item => item.key),
            urgencies: options.urgencies || leadsByUrgency.map(item => item.key)
        };
    }, [availableFilterOptions, leadsBySource, leadsByReferringSource, leadsByContactType, leadsByServiceType, leadsByJobType, leadsByUrgency]);

    // Business hours info popover state
    const [businessHoursAnchor, setBusinessHoursAnchor] = useState<null | HTMLElement>(null);
    const businessHoursPopoverOpen = Boolean(businessHoursAnchor);

    const handleBusinessHoursClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setBusinessHoursAnchor(event.currentTarget);
    }, []);

    const handleBusinessHoursClose = useCallback(() => {
        setBusinessHoursAnchor(null);
    }, []);

    const showPlaceGuidance =
        businessHoursSource === BusinessHoursSource.Default &&
        placeConfigStatus &&
        placeConfigStatus !== "loading" &&
        placeConfigStatus !== "configured";

    // Render the dashboard charts content
    const renderCharts = () => (
        <div className={styles.dashboardContent}>
            {/* Summary Metrics - Row 1 */}
            <div className={styles.metricsRow}>
                <Metric title="Total Engagements" loading={loading}>
                    {totalLeads !== undefined ? totalLeads.toLocaleString() : 'N/A'}
                </Metric>
                <Metric title="Total Service Requests" loading={loading}>
                    {totalServiceRequests.toLocaleString()}
                </Metric>
                <Metric title="Total Quote Requests" loading={loading}>
                    {totalQuoteRequests.toLocaleString()}
                </Metric>
                <Metric title="Other Requests" loading={loading}>
                    {totalOtherRequests.toLocaleString()}
                </Metric>
            </div>

            {/* Leads by Organization - only at global level */}
            {leadsByOrganization && leadsByOrganization.length > 0 && (
                <div className={styles.fullWidthChart}>
                    <h3>Top Engagements by Organization <span style={{ fontSize: "13px", fontWeight: "normal", color: "#999" }}>(Showing top {leadsByOrganization.length})</span></h3>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid #e0e0e0", textAlign: "left" }}>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333" }}>Organization</th>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333", textAlign: "right" }}>Total Engagements</th>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333", textAlign: "right" }}>High Value Leads</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leadsByOrganization.map((org) => (
                                <tr key={org.organizationId} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                    <td style={{ padding: "10px 12px", color: "#333" }}>
                                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {org.name}
                                            <Tooltip title="Organization Dashboard">
                                                <RouterLink
                                                    to={`/${org.organizationId}/dashboard`}
                                                    style={{ display: "inline-flex", color: "#666" }}
                                                >
                                                    <HomeIcon sx={{ fontSize: 18 }} />
                                                </RouterLink>
                                            </Tooltip>
                                            <Tooltip title="View Leads">
                                                <RouterLink
                                                    to={`/${org.organizationId}/leads`}
                                                    style={{ display: "inline-flex", color: "#666" }}
                                                >
                                                    <ContactPageIcon sx={{ fontSize: 18 }} />
                                                </RouterLink>
                                            </Tooltip>
                                            {onOrganizationFilterChange && (
                                                <Tooltip title="Filter by this organization">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            // Add this org to the filter (or set it as the only filter)
                                                            const newIds = selectedOrganizationIds.includes(org.organizationId)
                                                                ? selectedOrganizationIds
                                                                : [...selectedOrganizationIds, org.organizationId];
                                                            onOrganizationFilterChange(newIds);
                                                        }}
                                                        sx={{
                                                            padding: '2px',
                                                            color: selectedOrganizationIds.includes(org.organizationId) ? '#1976d2' : '#666'
                                                        }}
                                                    >
                                                        <FilterListIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </span>
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 500 }}>
                                        {org.leadCount.toLocaleString()}
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 500, color: org.highValueLeadCount > 0 ? "#2e7d32" : "#999" }}>
                                        {org.highValueLeadCount.toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Leads by App */}
            {leadsByApp && leadsByApp.length > 0 && (
                <div className={styles.fullWidthChart}>
                    <h3>Top Engagements by App <span style={{ fontSize: "13px", fontWeight: "normal", color: "#999" }}>(Showing top {leadsByApp.length})</span></h3>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid #e0e0e0", textAlign: "left" }}>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333" }}>Name</th>
                                {showOrganizationFilter && (
                                    <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333" }}>Organization</th>
                                )}
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333", textAlign: "right" }}>Total Engagements</th>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333", textAlign: "right" }}>High Value Leads</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leadsByApp.map((app) => {
                                // Use app.organizationId if available (global view), otherwise fall back to prop
                                const appOrgId = app.organizationId || organizationId;
                                return (
                                    <tr key={app.appId} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                        <td style={{ padding: "10px 12px", color: "#333" }}>
                                            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                {app.name}
                                                {appOrgId && (
                                                    <>
                                                        <Tooltip title="App Home">
                                                            <RouterLink
                                                                to={`/${appOrgId}/${app.appId}/`}
                                                                style={{ display: "inline-flex", color: "#666" }}
                                                            >
                                                                <HomeIcon sx={{ fontSize: 18 }} />
                                                            </RouterLink>
                                                        </Tooltip>
                                                        <Tooltip title="View Leads">
                                                            <RouterLink
                                                                to={`/${appOrgId}/leads?appId=${app.appId}`}
                                                                style={{ display: "inline-flex", color: "#666" }}
                                                            >
                                                                <ContactPageIcon sx={{ fontSize: 18 }} />
                                                            </RouterLink>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </span>
                                        </td>
                                        {showOrganizationFilter && (
                                            <td style={{ padding: "10px 12px", color: "#666" }}>
                                                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    {app.organizationName || app.organizationId || '-'}
                                                    {app.organizationId && onOrganizationFilterChange && (
                                                        <Tooltip title="Filter by this organization">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    const newIds = selectedOrganizationIds.includes(app.organizationId!)
                                                                        ? selectedOrganizationIds
                                                                        : [...selectedOrganizationIds, app.organizationId!];
                                                                    onOrganizationFilterChange(newIds);
                                                                }}
                                                                sx={{
                                                                    padding: '2px',
                                                                    color: selectedOrganizationIds.includes(app.organizationId!) ? '#1976d2' : '#666'
                                                                }}
                                                            >
                                                                <FilterListIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </span>
                                            </td>
                                        )}
                                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 500 }}>
                                            {app.leadCount.toLocaleString()}
                                        </td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 500, color: app.highValueLeadCount > 0 ? "#2e7d32" : "#999" }}>
                                            {app.highValueLeadCount.toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Top Engagements by Zip Code */}
            {(loading || (topEngagementsByZip && topEngagementsByZip.length > 0)) && (
                <div className={styles.fullWidthChart}>
                    <h3>Top Engagements by Zip Code {!loading && topEngagementsByZip && (
                        <span style={{ fontSize: "13px", fontWeight: "normal", color: "#999" }}>
                            {zipRowsExpanded || topEngagementsByZip.length <= COLLAPSED_ZIP_ROW_COUNT
                                ? `(Showing top ${topEngagementsByZip.length})`
                                : `(Showing top ${COLLAPSED_ZIP_ROW_COUNT} of ${topEngagementsByZip.length})`}
                        </span>
                    )}</h3>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid #e0e0e0", textAlign: "left" }}>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333" }}>Zip Code</th>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333", textAlign: "right" }}>Total Engagements</th>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333", textAlign: "right" }}>High Value Leads</th>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333", textAlign: "right" }}>% of All Leads</th>
                                <th style={{ padding: "10px 12px", fontWeight: 600, color: "#333", textAlign: "right" }}>Locations</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !topEngagementsByZip && (
                                <>
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <tr key={`skeleton-${i}`} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                            <td style={{ padding: "10px 12px" }}><div className={styles.skeletonRowShort} /></td>
                                            <td style={{ padding: "10px 12px" }}><div className={styles.skeletonRowMedium} style={{ marginLeft: "auto" }} /></td>
                                            <td style={{ padding: "10px 12px" }}><div className={styles.skeletonRowMedium} style={{ marginLeft: "auto" }} /></td>
                                            <td style={{ padding: "10px 12px" }}><div className={styles.skeletonRowMedium} style={{ marginLeft: "auto" }} /></td>
                                            <td style={{ padding: "10px 12px" }}><div className={styles.skeletonRowMedium} style={{ marginLeft: "auto" }} /></td>
                                        </tr>
                                    ))}
                                </>
                            )}
                            {visibleZipEngagements && visibleZipEngagements.map((zip) => (
                                <tr key={zip.zipCode} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                    <td style={{ padding: "10px 12px", color: "#333" }}>
                                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {zip.zipCode}
                                            {onFilterApply && (
                                                <Tooltip title="Filter dashboard by this zip code">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleZipFilterClick(zip.zipCode)}
                                                        sx={{
                                                            padding: '2px',
                                                            color: activeFilters.zipCode === zip.zipCode ? '#1976d2' : '#666'
                                                        }}
                                                    >
                                                        <FilterListIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </span>
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 500 }}>
                                        {zip.leadCount.toLocaleString()}
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 500, color: zip.highValueLeadCount > 0 ? "#2e7d32" : "#999" }}>
                                        {zip.highValueLeadCount.toLocaleString()}
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#666" }}>
                                        {zip.percentage.toFixed(1)}%
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#666" }}>
                                        {zip.uniqueApps.toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!loading && topEngagementsByZip && topEngagementsByZip.length > COLLAPSED_ZIP_ROW_COUNT && (
                        <Link
                            component="button"
                            variant="body2"
                            aria-expanded={zipRowsExpanded}
                            onClick={handleToggleZipRowsExpanded}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                marginTop: '8px',
                                fontSize: '13px',
                                color: '#666',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                '&:hover': {
                                    color: '#1976d2',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            {zipRowsExpanded ? 'Show less' : `Show all (${topEngagementsByZip.length})`}
                        </Link>
                    )}
                </div>
            )}

            {/* Place configuration guidance - shown when using default business hours */}
            {showPlaceGuidance && (
                <InlineAlert severity="info" title="Improve Your Business Hours Data">
                    {placeConfigStatus === "no-place" && (
                        <>
                            Add a Google Business Profile place to get accurate business hours and timezone-based reporting.
                            {businessHoursSettingsUrl && (
                                <>{" "}<Link component={RouterLink} to={businessHoursSettingsUrl} sx={{ fontWeight: "bold" }}>Add a place &rarr;</Link></>
                            )}
                        </>
                    )}
                    {placeConfigStatus === "missing-address" && (
                        <>
                            Your place needs an address with a zip code for accurate timezone and business hours reporting.
                            {businessHoursSettingsUrl && (
                                <>{" "}<Link component={RouterLink} to={businessHoursSettingsUrl} sx={{ fontWeight: "bold" }}>Update place settings &rarr;</Link></>
                            )}
                        </>
                    )}
                    {placeConfigStatus === "missing-hours" && (
                        <>
                            Your place doesn't have business hours yet. Try refreshing your place data to pull hours from your Google Business Profile.
                            {onRefreshPlace && (
                                <>{" "}
                                    <Link
                                        component="button"
                                        variant="body2"
                                        onClick={onRefreshPlace}
                                        disabled={refreshingPlace}
                                        sx={{
                                            fontWeight: "bold",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            verticalAlign: "baseline"
                                        }}
                                    >
                                        {refreshingPlace ? (
                                            <><CircularProgress size={14} sx={{ mr: 0.5 }} /> Refreshing...</>
                                        ) : (
                                            <><RefreshIcon sx={{ fontSize: 16 }} /> Refresh place data</>
                                        )}
                                    </Link>
                                </>
                            )}
                        </>
                    )}
                </InlineAlert>
            )}
            {/* Contacts by Day of Week (Business Hours Breakdown) */}
            <div className={styles.fullWidthChart}>
                <h3>Contacts by Day of Week (Local Business Time)</h3>
                <StackedBarChart
                    data={dayOfWeekChartData}
                    primaryLabel="Business Hours"
                    secondaryLabel="Non-Business Hours"
                    primaryColor="#2196f3"
                    secondaryColor="#bdbdbd"
                    height={CHART_DIMENSIONS.BAR_CHART_HEIGHT}
                    loading={loading}
                    summaryText={outsideBusinessHoursSummary}
                    ariaLabel="Contacts by day of week showing business hours breakdown"
                />
                {businessHoursSummary && businessHoursSummary.length > 0 && (
                    <>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={handleBusinessHoursClick}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                marginTop: '8px',
                                fontSize: '13px',
                                color: '#666',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                '&:hover': {
                                    color: '#1976d2',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            <HelpOutlineIcon sx={{ fontSize: '16px' }} />
                            How did we calculate this?
                        </Link>
                        <Popover
                            open={businessHoursPopoverOpen}
                            anchorEl={businessHoursAnchor}
                            onClose={handleBusinessHoursClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <div style={{ padding: '16px', maxWidth: '320px' }}>
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600 }}>
                                    Business Hours
                                    {businessHoursSource && (
                                        <span style={{
                                            marginLeft: '8px',
                                            fontSize: '11px',
                                            fontWeight: 'normal',
                                            color: '#666',
                                            textTransform: 'capitalize'
                                        }}>
                                            (Source: {businessHoursSource.toLowerCase()})
                                        </span>
                                    )}
                                </h4>
                                <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: '13px', lineHeight: '1.6' }}>
                                    {businessHoursSummary.map((line, index) => (
                                        <li key={index} style={{ color: '#333' }}>{line}</li>
                                    ))}
                                </ul>
                            </div>
                        </Popover>
                    </>
                )}
            </div>

            {/* Word Cloud */}
            <div className={styles.fullWidthChart}>
                <h3>Top Terms - Consumer Keywords</h3>
                <WordCloud
                    terms={topTerms}
                    maxTerms={MAX_WORD_CLOUD_TERMS}
                    height={WORD_CLOUD_HEIGHT}
                />
            </div>

            {/* Contact Type Charts */}
            <div className={styles.fullWidthChart}>
                <h3>Contact Type</h3>
                <div className={styles.dualChartContainer}>
                    <HorizontalBarChart
                        data={contactTypeData}
                        height={CHART_DIMENSIONS.HEIGHT}
                        loading={loading}
                        ariaLabel="Contact type breakdown"
                        onBarClick={createChartClickHandler('contactType')}
                    />
                    <DonutChart
                        data={contactTypeData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={false}
                        loading={loading}
                        ariaLabel="Contact type distribution"
                        onSegmentClick={createChartClickHandler('contactType')}
                    />
                </div>
            </div>

            {/* How Leads Reach Us - lead taxonomy: contact method, agent type, capture platform/system/surface */}
            <div className={styles.chartsGrid}>
                <div className={styles.chartCard}>
                    <DonutChart
                        title="Contact Method"
                        maxSlices={MAX_PIE_SLICES}
                        data={contactMethodData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={true}
                        legendPosition="bottom"
                        loading={loading}
                        ariaLabel="Leads by contact method"
                        onSegmentClick={createChartClickHandler('contactMethod')}
                    />
                </div>

                <div className={styles.chartCard}>
                    <DonutChart
                        title="Agent Type"
                        maxSlices={MAX_PIE_SLICES}
                        data={agentTypeData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={true}
                        legendPosition="bottom"
                        loading={loading}
                        ariaLabel="Leads by agent type"
                        onSegmentClick={createChartClickHandler('agentType')}
                    />
                </div>
            </div>

            <div className={styles.chartsGrid}>
                <div className={styles.chartCard}>
                    <DonutChart
                        title="Capture Platform"
                        maxSlices={MAX_PIE_SLICES}
                        data={capturePlatformData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={true}
                        legendPosition="bottom"
                        loading={loading}
                        ariaLabel="Leads by capture platform"
                        onSegmentClick={createChartClickHandler('capturePlatform')}
                    />
                </div>

                <div className={styles.chartCard}>
                    <DonutChart
                        title="Source System"
                        maxSlices={MAX_PIE_SLICES}
                        data={sourceSystemData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={true}
                        legendPosition="bottom"
                        loading={loading}
                        ariaLabel="Leads by source system"
                        onSegmentClick={createChartClickHandler('sourceSystem')}
                    />
                </div>
            </div>

            <div className={styles.fullWidthChart}>
                <h3>Capture Surface</h3>
                <div className={styles.dualChartContainer}>
                    <HorizontalBarChart
                        data={captureSurfaceData}
                        height={CHART_DIMENSIONS.HEIGHT}
                        loading={loading}
                        ariaLabel="Leads by capture surface breakdown"
                    />
                    <DonutChart
                        data={captureSurfaceData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={false}
                        loading={loading}
                        ariaLabel="Leads by capture surface distribution"
                    />
                </div>
            </div>

            {/* Row 3: Channel, Referring Source */}
            <div className={styles.chartsGrid}>
                {/* Leads by Channel (formerly Source) */}
                <div className={styles.chartCard}>
                    <DonutChart
                        title="Channel"
                        maxSlices={MAX_PIE_SLICES}
                        data={sourceData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={true}
                        legendPosition="bottom"
                        loading={loading}
                        ariaLabel="Leads by channel"
                        onSegmentClick={createChartClickHandler('source')}
                    />
                </div>

                {/* Leads by Referring Source */}
                <div className={styles.chartCard}>
                    <DonutChart
                        title="Referring Source"
                        maxSlices={MAX_PIE_SLICES}
                        data={referringSourceData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={true}
                        legendPosition="bottom"
                        loading={loading}
                        ariaLabel="Leads by referring source"
                        onSegmentClick={createChartClickHandler('referringSource')}
                    />
                </div>
            </div>

            {/* Marketing Attribution - utm source/medium/campaign. Sparse on most apps
                (~3% of leads, form-widget/abandoned captures only), so the whole cluster
                is omitted rather than showing permanently-blank cards. */}
            {hasMarketingAttributionData && (
                <div className={styles.chartsGrid}>
                    {(loading || utmSourceData.length > 0) && (
                        <div className={styles.chartCard}>
                            <DonutChart
                                title="UTM Source"
                                maxSlices={MAX_PIE_SLICES}
                                data={utmSourceData}
                                variant="pie"
                                width={CHART_DIMENSIONS.WIDTH}
                                height={CHART_DIMENSIONS.HEIGHT}
                                showLegend={true}
                                legendPosition="bottom"
                                loading={loading}
                                ariaLabel="Leads by UTM source"
                            />
                        </div>
                    )}

                    {(loading || utmMediumData.length > 0) && (
                        <div className={styles.chartCard}>
                            <DonutChart
                                title="UTM Medium"
                                maxSlices={MAX_PIE_SLICES}
                                data={utmMediumData}
                                variant="pie"
                                width={CHART_DIMENSIONS.WIDTH}
                                height={CHART_DIMENSIONS.HEIGHT}
                                showLegend={true}
                                legendPosition="bottom"
                                loading={loading}
                                ariaLabel="Leads by UTM medium"
                            />
                        </div>
                    )}

                </div>
            )}

            {/* UTM Campaign sits in its own full-width row: chartsGrid is a fixed 2-column grid, so
                a third card would leave a half-width gap beside it whenever all three have data (or
                during loading, when all three render). Matches the Capture Surface treatment above. */}
            {hasMarketingAttributionData && (loading || utmCampaignData.length > 0) && (
                <div className={styles.fullWidthChart}>
                    <div className={styles.chartCard}>
                        <DonutChart
                            title="UTM Campaign"
                            maxSlices={MAX_PIE_SLICES}
                            data={utmCampaignData}
                            variant="pie"
                            width={CHART_DIMENSIONS.WIDTH}
                            height={CHART_DIMENSIONS.HEIGHT}
                            showLegend={true}
                            legendPosition="bottom"
                            loading={loading}
                            ariaLabel="Leads by UTM campaign"
                        />
                    </div>
                </div>
            )}

            {/* Row 4: Urgency, High Value Leads */}
            <div className={styles.chartsGrid}>
                {/* Leads by Urgency */}
                <div className={styles.chartCard}>
                    <DonutChart
                        title="Urgency"
                        maxSlices={MAX_PIE_SLICES}
                        data={urgencyData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={true}
                        legendPosition="bottom"
                        loading={loading}
                        ariaLabel="Leads by urgency level"
                        onSegmentClick={createChartClickHandler('urgency')}
                    />
                </div>

                {/* High Value Leads */}
                <div className={styles.chartCard}>
                    <DonutChart
                        title="High Value Leads"
                        maxSlices={MAX_PIE_SLICES}
                        data={highValueLeadsData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={true}
                        legendPosition="bottom"
                        loading={loading}
                        ariaLabel="High value vs regular leads"
                        onSegmentClick={createChartClickHandler('highValueLead')}
                    />
                </div>
            </div>

            {/* Service Type Charts */}
            <div className={styles.fullWidthChart}>
                <h3>Service Type</h3>
                <div className={styles.dualChartContainer}>
                    <HorizontalBarChart
                        data={serviceTypeData}
                        height={CHART_DIMENSIONS.HEIGHT}
                        loading={loading}
                        ariaLabel="Leads by service type breakdown"
                        onBarClick={createChartClickHandler('serviceType')}
                    />
                    <DonutChart
                        data={serviceTypeData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={false}
                        loading={loading}
                        ariaLabel="Leads by service type distribution"
                        onSegmentClick={createChartClickHandler('serviceType')}
                    />
                </div>
            </div>

            {/* Job Types */}
            <div className={styles.fullWidthChart}>
                <h3>Job Types</h3>
                <div className={styles.dualChartContainer}>
                    <HorizontalBarChart
                        data={jobTypeData}
                        height={CHART_DIMENSIONS.BAR_CHART_HEIGHT}
                        loading={loading}
                        ariaLabel="Leads by job type breakdown"
                        onBarClick={createChartClickHandler('jobType')}
                    />
                    <DonutChart
                        data={jobTypeData}
                        variant="pie"
                        width={CHART_DIMENSIONS.WIDTH}
                        height={CHART_DIMENSIONS.HEIGHT}
                        showLegend={false}
                        loading={loading}
                        ariaLabel="Leads by job type distribution"
                        onSegmentClick={createChartClickHandler('jobType')}
                    />
                </div>
            </div>
        </div>
    );

    // When embedded, render just the charts (no header/date controls)
    if (embedded) {
        return renderCharts();
    }

    return (
        <section className={styles.dashboardSection}>
            <PageHeader
                title="Leads Dashboard"
                below={
                    <DashboardFilterBar
                        className={styles.filterBar}
                        totalItems={totalLeads}
                        activeFilters={activeFilters}
                        availableFilterOptions={derivedFilterOptions}
                        onFilterApply={onFilterApply}
                        onFilterClear={onFilterClear}
                        onClearAllFilters={onClearAllFilters}
                        onViewLeads={onViewLeads}
                        showOrganizationFilter={showOrganizationFilter}
                        availableOrganizations={availableOrganizations}
                        selectedOrganizationIds={selectedOrganizationIds}
                        onOrganizationFilterChange={onOrganizationFilterChange}
                    />
                }
            >
                <DashboardDateFilter startDate={start} endDate={end} onDateRangeChange={handleDateRangeChange} />
            </PageHeader>

            {renderCharts()}
        </section>
    );
};
